import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { NodeService } from 'src/app/services/node.service';
import { NodeDTO } from 'src/app/model/nodes/node-dto';
import { PaginatorComponent } from 'src/app/components/paginator/paginator.component';
import { MatDialog } from '@angular/material/dialog';
import { NodeConfigurationDialogComponent } from '../node-configuration-dialog/node-configuration-dialog.component';
import { LoadIndicatorService } from 'src/app/services/load-indicator.service';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { AppComponent } from 'src/app/app.component';
import { Router } from '@angular/router';
import { Utils } from 'src/app/utils/utils';

@Component({
  selector: 'app-node-list',
  templateUrl: './node-manager.component.html',
  styleUrls: ['./node-manager.component.scss'],
})
export class NodeManagerComponent implements AfterViewInit {
  public pageSize = 8;
  public currentPage: number = 1;
  public order = 'RecentFirst';
  public validated: boolean | undefined = undefined;
  public activated: boolean | undefined = undefined;
  public currentPageKey = 'nodemanager_currentPage';
  public validatedKey = 'nodemanager_validated';
  public activatedKey = 'nodemanager_activated';

  public nodes: NodeDTO[] = [];

  @ViewChild('nodeListPaginator')
  public paginator?: PaginatorComponent;

  constructor(
    public router: Router,
    public nodeService: NodeService,
    public dialog: MatDialog,
    private readonly loadIndicatorService: LoadIndicatorService,
    private readonly toastr: ToastrService,
  ) {
    AppComponent.updateAppHeader('node manager');
    this.loadNodeManagerParameters();
  }

  loadNodeManagerParameters(): void {
    let currentPageValue = localStorage.getItem(this.currentPageKey);
    if (currentPageValue != null) {
      this.currentPage = +(currentPageValue ?? 1);
    }

    let validatedValue = localStorage.getItem(this.validatedKey);
    if (validatedValue != null) {
      this.validated = validatedValue === 'true';
      localStorage.setItem(this.validatedKey, this.validated?.toString());
    }

    let activatedValue = localStorage.getItem(this.activatedKey);
    if (activatedValue != null) {
      this.activated = activatedValue === 'true';
      localStorage.setItem(this.activatedKey, this.activated?.toString());
    }
  }

  saveNodeManagerParameters(): void {
    localStorage.setItem(this.currentPageKey, this.currentPage.toString());

    if (this.validated == null) {
      localStorage.removeItem(this.validatedKey);
    } else {
      localStorage.setItem(this.validatedKey, this.validated!.toString());
    }

    if (this.activated == null) {
      localStorage.removeItem(this.activatedKey);
    } else {
      localStorage.setItem(this.activatedKey, this.activated!.toString());
    }
  }

  ngAfterViewInit(): void {
    if (this.paginator) {
      this.paginator!.selectedPage = this.currentPage;
      this.loadNodes();
    }
  }

  resetCurrentPage(): void {
    this.paginator!.selectedPage = 1;
    this.currentPage = 1;
  }

  getNodeStatus(str: string | undefined): string {
    if (!str) return '-';

    return str
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .toLowerCase()
      .replace(/^./, (c) => c.toUpperCase());
  }

  goToNodeHistory(nodeId: number): void {
    this.router.navigate(['node-manager/history', nodeId]);
  }

  public loadNodes(): void {
    this.loadIndicatorService.show();
    this.currentPage = this.paginator?.selectedPage ?? 1;
    this.saveNodeManagerParameters();
    this.nodeService
      .getNodes(
        this.currentPage,
        this.pageSize,
        this.order,
        this.validated,
        this.activated,
      )
      .then((response) => {
        this.paginator?.updateData(response.totalPages, response.pageSize);

        if (response.totalPages < this.currentPage) {
          this.paginator!.selectedPage = response.totalPages;
          this.loadNodes();
        } else {
          this.nodes = response.items;
          this.nodes.forEach((node) => {
            if (node.lastUploadTimestamp != null) {
              let utcDateString = Utils.prepareUTCDateString(
                node.lastUploadTimestamp,
              );
              node.lastUploadTimestamp = new Date(utcDateString);
            }
          });
        }
      })
      .finally(() => {
        this.loadIndicatorService.hide();
      });
  }

  public validate(node: NodeDTO): void {
    this.loadIndicatorService.show();
    this.nodeService
      .validate(node.id)
      .then(() => {
        node.validated = true;
        this.toastr.success('Node validated', 'Success');
      })
      .catch(() => {
        this.toastr.error('Unable to validate node', 'Error');
      })
      .finally(() => {
        this.loadIndicatorService.hide();
      });
  }

  public unvalidate(node: NodeDTO): void {
    this.loadIndicatorService.show();
    this.nodeService
      .unvalidate(node.id)
      .then(() => {
        node.validated = false;
        this.toastr.success('Node unvalidated', 'Success');
      })
      .catch(() => {
        this.toastr.error('Unable to unvalidate node', 'Error');
      })
      .finally(() => {
        this.loadIndicatorService.hide();
      });
  }

  configureNode(node: NodeDTO): void {
    this.router.navigate(['/node-manager/configure', node.id]);
  }

  nodeCanBeConfigured(node: NodeDTO): boolean {
    return (
      this.nodeIsActive(node.lastUploadTimestamp) &&
      node.status !== null &&
      node.status !== undefined
    );
  }

  viewNodeLifecycle(node: NodeDTO): void {
    if (this.nodeIsActive(node.lastUploadTimestamp)) {
      this.router.navigate([
        '/node-manager/lifecycle',
        node.id,
        node.nodeIdentifier,
      ]);
    }
  }

  showConfigurationDialog(node: NodeDTO): void {
    this.dialog.open(NodeConfigurationDialogComponent, {
      data: node.id,
    });
  }

  public onParametersChanged() {
    this.resetCurrentPage();
    this.loadNodes();
  }

  public nodeIsActive(timestamp?: Date) {
    // if this must to be used in other place, create utils
    if (timestamp) {
      let date = new Date(timestamp);
      let differenceInMinutes = Date.now() - date.getTime();
      differenceInMinutes /= 60000;
      return differenceInMinutes <= environment.nodeInactiveThreshold;
    } else {
      return false;
    }
  }
}
